import React, { useState } from 'react';
import { Linking } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { AuthStackParamList } from '../../navigation/AuthNavigator';
import { auth, createUserWithEmailAndPassword, signInAnonymously } from '../../config/firebase';
import { ProfileData } from '../../services/discoveryOnboardingAPI';
import { useNavigationLogging } from '../../hooks/useNavigationLogging';
import { completeOnboarding as markOnboardingComplete } from '../../utils/onboardingStorage';
import { showAlert } from '../../utils/alert';
import { EmailAuthProvider, linkWithCredential } from 'firebase/auth';
import { signInWithApple, signInWithGoogle } from '../../services/authService';
import { errorReporting } from '../../services/errorReportingService';
import {
  FirebaseProfile,
  fetchOldProfileData,
  handleSocialSignIn,
  migrateAnonymousProfile,
} from './authShared';
import AuthTemplate from './AuthTemplate';

type RegisterScreenNavigationProp = NativeStackNavigationProp<AuthStackParamList, 'Register'>;

interface RegisterScreenParams {
  profile?: ProfileData;
  userName?: string;
  fromOnboarding?: boolean; // Flag indicating user just completed onboarding
}

const TERMS_URL = 'https://www.mentorist.app/termsandconditions/';
const PRIVACY_URL = 'https://www.mentorist.app/privacy-policy/';

const RegisterScreen = () => {
  useNavigationLogging('RegisterScreen');
  
  const navigation = useNavigation<RegisterScreenNavigationProp>();
  const route = useRoute();
  
  const params = route.params as RegisterScreenParams | undefined;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [hasInteractedWithEmail, setHasInteractedWithEmail] = useState(false);
  const [hasInteractedWithPassword, setHasInteractedWithPassword] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const loadingTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  const validateEmail = (email: string) => {
    if (!email.trim()) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validatePassword = (password: string) => {
    if (!password.trim()) {
      return 'Password is required';
    }
    if (password.length < 6) {
      return 'Password must be at least 6 characters';
    }
    return '';
  };

  const handleEmailChange = (text: string) => {
    setEmail(text);
    if (hasInteractedWithEmail) {
      setEmailError(validateEmail(text));
    }
  };

  const handlePasswordChange = (text: string) => {
    setPassword(text);
    if (hasInteractedWithPassword) {
      setPasswordError(validatePassword(text));
    }
  };

  const handleEmailBlur = () => {
    setHasInteractedWithEmail(true);
    setEmailError(validateEmail(email));
  };

  const handlePasswordBlur = () => {
    setHasInteractedWithPassword(true);
    setPasswordError(validatePassword(password));
  };

  const isFormValid = () => {
    return validateEmail(email) === '' && validatePassword(password) === '';
  };

  const handleRegister = async () => {
    // Validate all fields and show errors
    const emailValidation = validateEmail(email);
    const passwordValidation = validatePassword(password);
    
    setEmailError(emailValidation);
    setPasswordError(passwordValidation);
    setHasInteractedWithEmail(true);
    setHasInteractedWithPassword(true);
    
    if (!isFormValid()) {
      return;
    }

    setLoading(true);
    try {
      const currentUser = auth.currentUser;
      let userCredential;
      
      // If user is anonymous, link credentials to keep the same user ID and all data
      if (currentUser && currentUser.isAnonymous) {
        const credential = EmailAuthProvider.credential(email, password);
        userCredential = await linkWithCredential(currentUser, credential);
      } else {
        // No anonymous user, create a new account
        userCredential = await createUserWithEmailAndPassword(auth, email, password);
      }
      
      await navigateAfterRegister();
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    } catch (error: any) {
      console.error('Registration error:', error);
      errorReporting.reportError(error, {
        userId: auth.currentUser?.uid,
        screen: 'RegisterScreen',
        action: 'handleRegister',
        metadata: {
          errorCode: error?.code,
          fromOnboarding: !!(route.params as any)?.fromOnboarding,
        },
      });
      
      // Helper to format error codes for user display
      const formatErrorCode = (code: string | undefined): string => {
        if (!code) return 'Unknown Error';
        return code.replace('auth/', '').split('-').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
      };
      
      // Extract actual error code from wrapped message or use direct code
      let actualErrorCode = error.code;
      const wrappedMessage = error?.customData?.message;
      if (wrappedMessage && typeof wrappedMessage === 'string') {
        const match = wrappedMessage.match(/auth\/[\w-]+/);
        if (match) {
          actualErrorCode = match[0];
        }
      }
      
      // Map common errors to friendly messages
      let errorMessage: string;
      switch (actualErrorCode) {
        case 'auth/email-already-in-use':
          errorMessage = 'That email address is already in use!';
          break;
        case 'auth/invalid-email':
          errorMessage = 'That email address is invalid!';
          break;
        case 'auth/weak-password':
          errorMessage = 'Password should be at least 6 characters.';
          break;
        case 'auth/operation-not-allowed':
          errorMessage = 'Account creation is currently disabled.';
          break;
        default:
          // For any other error, show the formatted error code
          errorMessage = formatErrorCode(actualErrorCode);
      }
      
      showAlert('Registration Failed', errorMessage);
      setLoading(false);
    }
  };

  const handleAnonymousLogin = async () => {
    setLoading(true);
    try {
      const userCredential = await signInAnonymously(auth);
      
      // If coming from onboarding, mark as complete and go to dashboard
      await navigateAfterRegister();
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    } catch (error: any) {
      showAlert('Error', 'Failed to sign in anonymously. Please try again.');
      setLoading(false);
    }
  };

  const navigateAfterRegister = async () => {
    if (params?.fromOnboarding) {
      await markOnboardingComplete();
      (navigation as any).reset({
        index: 0,
        routes: [{ name: 'Main' }],
      });
    } else if (params?.profile && params?.userName) {
      (navigation as any).navigate('ProfileSummary', {
        profile: params.profile,
        userName: params.userName,
      });
    } else {
      navigation.navigate('DiscoveryV2Onboarding');
    }
  };

  const handleAppleRegister = async () =>
    handleSocialSignIn({
      signIn: signInWithApple,
      providerLabel: 'Apple',
      loading: appleLoading,
      setLoading: setAppleLoading,
      onNavigateAfterAuth: async () => {
        await navigateAfterRegister();
      },
    });

  const handleGoogleRegister = async () =>
    handleSocialSignIn({
      signIn: signInWithGoogle,
      providerLabel: 'Google',
      loading: googleLoading,
      setLoading: setGoogleLoading,
      onNavigateAfterAuth: async () => {
        await navigateAfterRegister();
      },
    });

  const navigateToLogin = () => {
    navigation.navigate('Login');
  };

  const handleOpenExternalLink = async (url: string) => {
    try {
      const supported = await Linking.canOpenURL(url);
      if (!supported) {
        throw new Error('Unsupported URL');
      }
      await Linking.openURL(url);
    } catch (error) {
      showAlert('Unable to open link', 'Please try again later.');
    }
  };

  return (
    <AuthTemplate
      mode="register"
      title={params?.fromOnboarding ? 'Save Your Personalized Plan' : 'Create Account'}
      subtitle={params?.fromOnboarding ? 'Create an account to save your personalized mentorship plan and access it anytime.' : undefined}
      email={email}
      password={password}
      emailError={emailError}
      passwordError={passwordError}
      onEmailChange={handleEmailChange}
      onPasswordChange={handlePasswordChange}
      onEmailBlur={handleEmailBlur}
      onPasswordBlur={handlePasswordBlur}
      onEmailPasswordSubmit={handleRegister}
      emailPasswordLoading={loading}
      onApplePress={handleAppleRegister}
      appleLoading={appleLoading}
      onGooglePress={handleGoogleRegister}
      googleLoading={googleLoading}
      onSecondaryAction={navigateToLogin}
      secondaryActionLabel="Sign In"
      onAnonymousLogin={handleAnonymousLogin}
    />
  );
};

export default RegisterScreen; 